// components/leads/LeadCard.js
// Card singolo lead per Kanban - NUOVA UI migliorata con Multi-assegnazione

import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Phone, MapPin, User, FileText, MoreVertical, ArrowRight, X, GripVertical, HardHat, Sofa } from 'lucide-react';
import { leadStatusLabels, leadStatusColors, leadOpportunityTypeLabels, getBadgeClass } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

// Tutti gli stati disponibili per il menu "Sposta a..."
const allStatuses = [
  { status: 'nuovo', label: 'Nuovo' },
  { status: 'contattato', label: 'Contattato' },
  { status: 'assegnato', label: 'Assegnato' },
  { status: 'appuntamento_fissato', label: 'Appuntamento Fissato' },
  { status: 'sopralluogo_effettuato', label: 'Sopralluogo Effettuato' },
  { status: 'in_preventivazione', label: 'In Preventivazione' },
  { status: 'preventivo_presentato', label: 'Preventivo Presentato' },
  { status: 'in_trattativa', label: 'In Trattativa' },
  { status: 'in_contratto', label: 'In Contratto' },
  { status: 'convertito', label: 'Convertito' },
  { status: 'non_risponde', label: 'Non Risponde' },
  { status: 'perso', label: 'Perso' },
];

// Stati che richiedono modale specifica
const STATES_REQUIRING_MODAL = {
  assegnato: 'technician',
  appuntamento_fissato: 'appointment',
  perso: 'lost_reason',
};

const LeadCard = ({ 
  lead, 
  compact = false, 
  estimatesCount = 0,
  onShowDetail,
  onOpenEstimate,
  onTransition,
  onAssign,
  getTechnicianName,
  getAssigneeName,
  isDragging = false,
  showDragHandle = false,
  dragHandleProps = null
}) => {
  const { isAdmin, isProjectManager } = useAuth();
  const canMoveFreely = isAdmin || isProjectManager;
  
  const isLost = lead.status === 'perso' || lead.status === 'non_risponde';
  const isWon = lead.status === 'convertito';
  
  // Ottieni le destinazioni disponibili per il menu "Sposta a..."
  const getAvailableDestinations = () => {
    if (canMoveFreely) {
      return allStatuses.filter(s => s.status !== lead.status);
    } else {
      const validTransitions = {
        nuovo: ['contattato', 'non_risponde', 'perso'],
        contattato: ['assegnato', 'non_risponde', 'perso'],
        non_risponde: ['contattato', 'perso'],
        assegnato: ['appuntamento_fissato', 'perso'],
        appuntamento_fissato: ['sopralluogo_effettuato', 'perso'],
        sopralluogo_effettuato: ['in_preventivazione', 'perso'],
        in_preventivazione: ['preventivo_presentato', 'perso'],
        preventivo_presentato: ['in_trattativa', 'perso'],
        in_trattativa: ['in_contratto', 'perso'],
        in_contratto: ['convertito', 'perso'],
      };
      const allowed = validTransitions[lead.status] || [];
      return allStatuses.filter(s => allowed.includes(s.status));
    }
  };
  
  const handleMoveToStatus = (targetStatus) => {
    const modalType = STATES_REQUIRING_MODAL[targetStatus];
    
    if (modalType === 'technician') {
      onShowDetail(lead);
      setTimeout(() => onAssign && onAssign(), 100);
    } else if (modalType === 'appointment' || modalType === 'lost_reason') {
      onTransition && onTransition({ 
        lead, 
        action: { 
          status: targetStatus, 
          label: allStatuses.find(s => s.status === targetStatus)?.label || targetStatus,
          requiresNote: modalType === 'lost_reason'
        } 
      });
    } else {
      onTransition && onTransition({ 
        lead, 
        action: { 
          status: targetStatus, 
          label: allStatuses.find(s => s.status === targetStatus)?.label || targetStatus
        } 
      });
    }
  };
  
  const availableDestinations = getAvailableDestinations();
  const normalDestinations = availableDestinations.filter(d => d.status !== 'perso' && d.status !== 'non_risponde');
  const negativeDestinations = availableDestinations.filter(d => d.status === 'perso' || d.status === 'non_risponde');

  // Formatta il nome completo
  const fullName = `${lead.first_name || ''} ${lead.last_name || ''}`.trim();
  
  return (
    <div 
      className={`bg-white rounded-xl border-2 p-4 cursor-pointer transition-all hover:shadow-lg hover:border-[#FF7819]/50 ${
        isLost ? 'border-red-200 bg-red-50/50' : isWon ? 'border-green-200 bg-green-50/50' : 'border-gray-100'
      } ${isDragging ? 'opacity-50 ring-2 ring-[#FF7819] shadow-xl' : ''}`}
      onClick={() => onShowDetail && onShowDetail(lead)}
      data-testid={`lead-card-${lead.id}`}
    >
      {/* HEADER: Grip + Nome + Menu */}
      <div className="flex items-start gap-2 mb-3">
        {/* Drag Handle - visibile solo su desktop per utenti autorizzati */}
        {showDragHandle && dragHandleProps && (
          <div
            {...dragHandleProps}
            className="hidden md:flex items-center justify-center w-6 h-6 shrink-0 cursor-grab active:cursor-grabbing text-gray-300 hover:text-[#FF7819] transition-colors mt-0.5 rounded hover:bg-gray-100"
            onClick={(e) => e.stopPropagation()}
            title="Trascina per spostare"
            data-testid={`lead-drag-handle-${lead.id}`}
          >
            <GripVertical className="w-4 h-4" />
          </div>
        )}
        
        {/* Nome completo - massima priorità visiva */}
        <div className="flex-1 min-w-0">
          <h3 
            className="font-bold text-[#3C3C3C] text-base leading-snug"
            style={{ 
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              wordBreak: 'break-word'
            }}
            title={fullName}
          >
            {fullName || 'Nome non disponibile'}
          </h3>
        </div>
        
        {/* Menu tre puntini */}
        {!isLost && !isWon && availableDestinations.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 shrink-0 hover:bg-gray-100"
                onClick={(e) => e.stopPropagation()}
                data-testid={`lead-card-menu-${lead.id}`}
              >
                <MoreVertical className="w-4 h-4 text-gray-500" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <div className="px-2 py-1.5 text-xs font-medium text-gray-500">
                Sposta a...
              </div>
              {normalDestinations.map(dest => (
                <DropdownMenuItem
                  key={dest.status}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMoveToStatus(dest.status);
                  }}
                  className="cursor-pointer"
                >
                  <ArrowRight className="w-3.5 h-3.5 mr-2 text-[#FF7819]" />
                  {dest.label}
                </DropdownMenuItem>
              ))}
              {negativeDestinations.length > 0 && normalDestinations.length > 0 && (
                <DropdownMenuSeparator />
              )}
              {negativeDestinations.map(dest => (
                <DropdownMenuItem
                  key={dest.status}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMoveToStatus(dest.status);
                  }}
                  className="cursor-pointer text-red-600 focus:text-red-600"
                >
                  <X className="w-3.5 h-3.5 mr-2" />
                  {dest.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* BODY: Info contatto - più leggibili */}
      <div className="space-y-2 text-sm mb-3">
        {/* Badge Tipo Opportunità - Solo se definito */}
        {lead.lead_opportunity_type && lead.lead_opportunity_type !== 'non_definito' && (
          <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
            lead.lead_opportunity_type === 'lavori' ? 'bg-[#E5F0FF] text-[#0078C8]' :
            lead.lead_opportunity_type === 'arredi' ? 'bg-[#E5F7ED] text-[#008A3D]' :
            lead.lead_opportunity_type === 'lavori_arredi' ? 'bg-[#FFF9E5] text-[#B38C00]' :
            'bg-[#F5F5F5] text-[#737373]'
          }`}>
            {(lead.lead_opportunity_type === 'lavori' || lead.lead_opportunity_type === 'lavori_arredi') && (
              <HardHat className="w-3 h-3" />
            )}
            {(lead.lead_opportunity_type === 'arredi' || lead.lead_opportunity_type === 'lavori_arredi') && (
              <Sofa className="w-3 h-3" />
            )}
            <span>{leadOpportunityTypeLabels[lead.lead_opportunity_type]}</span>
          </div>
        )}
        
        {/* Città/Indirizzo */}
        {(lead.property_city || lead.property_address) && (
          <div className="flex items-start gap-2 text-[#737373]">
            <MapPin className="w-4 h-4 shrink-0 mt-0.5 text-[#FF7819]" />
            <span className="leading-tight">
              {lead.property_address && <span className="block">{lead.property_address}</span>}
              {lead.property_city && <span className="font-medium text-[#3C3C3C]">{lead.property_city}</span>}
              {lead.property_cap && <span className="text-xs ml-1">({lead.property_cap})</span>}
            </span>
          </div>
        )}
        
        {/* Telefono - ben visibile */}
        {lead.phone && (
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 shrink-0 text-green-600" />
            <a 
              href={`tel:${lead.phone}`} 
              className="font-medium text-[#3C3C3C] hover:text-[#FF7819] transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              {lead.phone}
            </a>
          </div>
        )}
        
        {/* Responsabile Lavori - nuovo campo */}
        {(lead.assigned_work_user_id || lead.assigned_technician_id) && getAssigneeName && (
          (() => {
            const name = getAssigneeName(lead.assigned_work_user_id || lead.assigned_technician_id);
            // Non mostrare se non assegnato o utente sconosciuto
            if (!name || name === 'Non assegnato' || name === 'Utente sconosciuto') return null;
            return (
              <div className="flex items-center gap-2 text-[#737373]">
                <HardHat className="w-4 h-4 shrink-0 text-[#0078C8]" />
                <span className="truncate text-xs">{name}</span>
              </div>
            );
          })()
        )}
        
        {/* Fallback: Tecnico assegnato (legacy) - mostra solo se non c'è assigned_work_user_id */}
        {!lead.assigned_work_user_id && lead.assigned_technician_id && getTechnicianName && !getAssigneeName && (
          <div className="flex items-center gap-2 text-[#737373]">
            <User className="w-4 h-4 shrink-0 text-indigo-500" />
            <span className="truncate">{getTechnicianName(lead.assigned_technician_id)}</span>
          </div>
        )}
        
        {/* Responsabile Arredi - nuovo campo */}
        {lead.assigned_furniture_user_id && getAssigneeName && (
          (() => {
            const name = getAssigneeName(lead.assigned_furniture_user_id);
            // Non mostrare se non assegnato o utente sconosciuto
            if (!name || name === 'Non assegnato' || name === 'Utente sconosciuto') return null;
            return (
              <div className="flex items-center gap-2 text-[#737373]">
                <Sofa className="w-4 h-4 shrink-0 text-[#008A3D]" />
                <span className="truncate text-xs">{name}</span>
              </div>
            );
          })()
        )}
      </div>

      {/* FOOTER: Badge stato + Preventivi */}
      <div className="flex items-center justify-between gap-2 pt-3 border-t border-gray-100">
        <Badge 
          className={`text-xs px-2.5 py-1 ${
            isLost ? 'bg-red-100 text-red-700 border-red-200' :
            isWon ? 'bg-green-100 text-green-700 border-green-200' :
            getBadgeClass(leadStatusColors[lead.status])
          }`}
        >
          {leadStatusLabels[lead.status] || lead.status}
        </Badge>
        
        {/* Preventivi badge */}
        {estimatesCount > 0 && (
          <button
            className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-lg text-xs font-medium transition-colors border border-amber-200"
            onClick={(e) => {
              e.stopPropagation();
              onOpenEstimate && onOpenEstimate(lead.id);
            }}
            title={`${estimatesCount} preventiv${estimatesCount === 1 ? 'o' : 'i'}`}
            data-testid={`lead-estimates-badge-${lead.id}`}
          >
            <FileText className="w-3.5 h-3.5" />
            {estimatesCount}
          </button>
        )}
      </div>
    </div>
  );
};

export default LeadCard;
